const FOCUSABLE_SELECTOR = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

function getSlideLabel(slide, index, total) {
  const clone = slide.cloneNode(true);

  clone.querySelectorAll(FOCUSABLE_SELECTOR).forEach((node) => node.remove());

  const text = clone.textContent?.trim().replace(/\s+/g, ' ');

  if (text) {
    return `${text} (${index + 1} of ${total})`;
  }

  return `Slide ${index + 1} of ${total}`;
}

function getActiveSlideIndex(swiper) {
  const root = swiper.el?.closest?.('[data-kl-media-carousel]');

  if (root) {
    const mainSwiperEl = [...root.querySelectorAll('.swiper')].find(
      (el) => !el.className.includes('thumbs')
    );
    const mainRealIndex = mainSwiperEl?.swiper?.realIndex;

    if (typeof mainRealIndex === 'number' && mainRealIndex >= 0) {
      return mainRealIndex;
    }
  }

  const slides = swiper.slides ?? [];
  const thumbActive = slides.findIndex((slide) => slide.classList.contains('swiper-slide-thumb-active'));

  if (thumbActive >= 0) {
    return thumbActive;
  }

  return swiper.activeIndex ?? 0;
}

/**
 * Makes Contentful / CF thumb Swipers keyboard accessible.
 * Tab = focus outline only. Enter/Space = activate slide.
 * Mouse click: Swiper Thumbs module when `activateOnClick` is false; custom handler when true.
 */
export function applySwiperThumbTabsA11y(
  swiper,
  {label = 'Carousel thumbnails', onActivate, activateOnClick = false} = {}
) {
  if (!swiper?.el || swiper.destroyed) {
    return () => {};
  }

  const wrapper = swiper.el;

  wrapper.setAttribute('role', 'group');
  wrapper.setAttribute('aria-label', label);

  const slideKeyHandlers = new WeakMap();
  const slideClickHandlers = new WeakMap();
  let lastKeyboardActivationAt = 0;
  let updateSlidesRaf = 0;

  const scheduleUpdateSlides = () => {
    if (updateSlidesRaf) {
      cancelAnimationFrame(updateSlidesRaf);
    }

    updateSlidesRaf = requestAnimationFrame(() => {
      updateSlidesRaf = 0;
      updateSlides();
    });
  };

  const updateSlides = () => {
    const slides = swiper.slides ?? [];
    const total = slides.length;
    const activeIndex = getActiveSlideIndex(swiper);

    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;

      slide.setAttribute('role', 'button');
      slide.setAttribute('tabindex', '0');
      slide.setAttribute('aria-label', getSlideLabel(slide, index, total));

      if (isActive) {
        slide.setAttribute('aria-current', 'true');
      } else {
        slide.removeAttribute('aria-current');
      }
    });
  };

  const activateSlide = (index, {source = 'keyboard'} = {}) => {
    if (index < 0 || index >= (swiper.slides?.length ?? 0)) {
      return;
    }

    if (source === 'keyboard') {
      lastKeyboardActivationAt = Date.now();
    }

    onActivate?.(index);
    scheduleUpdateSlides();
  };

  const attachSlideListeners = () => {
    (swiper.slides ?? []).forEach((slide, index) => {
      const existingKey = slideKeyHandlers.get(slide);

      if (existingKey) {
        slide.removeEventListener('keydown', existingKey, true);
      }

      const existingClick = slideClickHandlers.get(slide);

      if (existingClick) {
        slide.removeEventListener('click', existingClick, true);
      }

      if (activateOnClick) {
        const onClick = (event) => {
          if (Date.now() - lastKeyboardActivationAt < 300) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          activateSlide(index, {source: 'click'});
        };

        slideClickHandlers.set(slide, onClick);
        slide.addEventListener('click', onClick, true);
      }

      const onKeyDown = (event) => {
        const slides = swiper.slides ?? [];
        const slideCount = slides.length;

        if (slideCount < 1) {
          return;
        }

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            event.preventDefault();
            slides[(index + 1) % slideCount]?.focus({preventScroll: true});
            return;
          case 'ArrowLeft':
          case 'ArrowUp':
            event.preventDefault();
            slides[(index - 1 + slideCount) % slideCount]?.focus({preventScroll: true});
            return;
          case 'Home':
            event.preventDefault();
            slides[0]?.focus({preventScroll: true});
            return;
          case 'End':
            event.preventDefault();
            slides[slideCount - 1]?.focus({preventScroll: true});
            return;
          case 'Enter':
          case ' ':
            event.preventDefault();
            event.stopPropagation();
            activateSlide(index, {source: 'keyboard'});
            return;
          default:
        }
      };

      slideKeyHandlers.set(slide, onKeyDown);
      slide.addEventListener('keydown', onKeyDown, true);
    });
  };

  const detachSlideListeners = () => {
    (swiper.slides ?? []).forEach((slide) => {
      const keyHandler = slideKeyHandlers.get(slide);

      if (keyHandler) {
        slide.removeEventListener('keydown', keyHandler, true);
      }

      const clickHandler = slideClickHandlers.get(slide);

      if (clickHandler) {
        slide.removeEventListener('click', clickHandler, true);
      }
    });
  };

  const handleSlideChange = () => {
    scheduleUpdateSlides();
  };

  const handleUpdate = () => {
    detachSlideListeners();
    updateSlides();
    attachSlideListeners();
  };

  updateSlides();
  attachSlideListeners();

  let attachedMainSwiper = null;

  const handleMainSlideChange = () => {
    scheduleUpdateSlides();
  };

  const tryAttachMainSwiperListener = () => {
    const root = wrapper.closest('[data-kl-media-carousel]');

    if (!root || attachedMainSwiper) {
      return;
    }

    const mainSwiperEl = [...root.querySelectorAll('.swiper')].find(
      (el) => !el.className.includes('thumbs')
    );
    const mainSwiper = mainSwiperEl?.swiper;

    if (!mainSwiper) {
      return;
    }

    attachedMainSwiper = mainSwiper;
    mainSwiper.on('slideChange', handleMainSlideChange);
  };

  tryAttachMainSwiperListener();
  requestAnimationFrame(tryAttachMainSwiperListener);
  setTimeout(tryAttachMainSwiperListener, 0);
  setTimeout(tryAttachMainSwiperListener, 300);

  swiper.on('slideChange', handleSlideChange);
  swiper.on('update', handleUpdate);

  return () => {
    if (updateSlidesRaf) {
      cancelAnimationFrame(updateSlidesRaf);
    }

    attachedMainSwiper?.off('slideChange', handleMainSlideChange);

    detachSlideListeners();
    swiper.off('slideChange', handleSlideChange);
    swiper.off('update', handleUpdate);
    wrapper.removeAttribute('role');
    wrapper.removeAttribute('aria-label');
    (swiper.slides ?? []).forEach((slide) => {
      slide.removeAttribute('role');
      slide.removeAttribute('aria-current');
      slide.removeAttribute('tabindex');
      slide.removeAttribute('aria-label');
    });
  };
}

export function isSwiperThumbVariant(variant = '', presets = '') {
  const presetList = Array.isArray(presets) ?
    presets :
    String(presets || '')
      .split(/[\n\s,]+/)
      .filter(Boolean);

  return /-Thumbs$/i.test(variant) || presetList.some((preset) => /-Thumbs$/i.test(preset));
}
